import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, ArrowRight, AlertCircle } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";

const BookingInquiry = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    preferred_city: "",
    desired_duration: "",
    screening_method: "",
    message: "",
    referral_source: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phone) {
      toast.error("Phone number is required.");
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/booking/inquiry`, formData);
      setIsSubmitted(true);
      toast.success("Inquiry submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="booking-success">
        <section className="min-h-screen flex items-center justify-center px-6 py-20">
          <div className="max-w-lg text-center animate-fade-in">
            <div className="w-20 h-20 bg-jade-deep rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="w-10 h-10 text-white" />
            </div>
            <T k="inquiry.inquiry-received" as="h1" className="font-heading text-4xl text-charcoal mb-6">Inquiry Received</T>
            <T k="inquiry.thank-you-for" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-8">Thank you for your interest in connecting. I personally review each inquiry and will respond within 24-48 hours if we seem like a mutual match.</T>
            <T k="inquiry.warmly-kalani" as="p" className="font-accent text-2xl text-champagne-gold mb-12">Warmly, Kalani</T>
            <Link to="/">
              <Button className="btn-secondary" data-testid="return-home-btn">
                Return Home
              </Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="booking-inquiry-page">
      {/* Header */}
      <section className="page-header" data-testid="inquiry-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="inquiry.lets-connect" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Let's Connect</T>
          <T k="inquiry.booking-inquiry" as="h1" className="page-title animate-fade-in stagger-1">Booking Inquiry</T>
          <T k="inquiry.begin-your-introduction" as="p" className="page-subtitle animate-fade-in stagger-2">Begin your introduction by sharing a bit about yourself and your ideal arrangement.</T>
        </div>
      </section>

      {/* Reminder */}
      <section className="py-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <div className="glass rounded-xl p-6 flex items-start gap-4">
            <div className="w-8 h-8 bg-champagne-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
              <ArrowRight className="w-4 h-4 text-champagne-gold" />
            </div>
            <p className="font-body text-sm text-stone-grey">
              Haven't read the booking guidelines yet? Please review the{" "}
              <Link to="/booking-etiquette" className="text-jade-text hover:text-champagne-gold underline">
                Booking Etiquette
              </Link>{" "}
              page before submitting your inquiry.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 px-6 md:px-12 lg:px-24" data-testid="inquiry-form-section">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div>
              <T k="inquiry.personal-information" as="h3" className="font-heading text-2xl text-charcoal mb-6">Personal Information</T>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-body text-sm text-stone-grey">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="How should I address you?"
                    className="form-input"
                    data-testid="inquiry-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body text-sm text-stone-grey">
                    Email Address <T k="inquiry.optional" className="text-taupe">(Optional)</T>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="form-input"
                    data-testid="inquiry-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-body text-sm text-stone-grey">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Your phone number"
                    className="form-input"
                    data-testid="inquiry-phone"
                  />
                  <div className="flex items-start gap-2 mt-2">
                    <AlertCircle className="w-4 h-4 text-lipstick-red flex-shrink-0 mt-0.5" />
                    <T k="inquiry.no-burner-numbers" as="p" className="font-body text-xs text-lipstick-red">No burner numbers allowed. Please provide a real, contactable phone number.</T>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="referral_source" className="font-body text-sm text-stone-grey">
                    How did you find me?
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange("referral_source", value)}>
                    <SelectTrigger className="form-input" data-testid="inquiry-referral">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="search_engine">Search Engine</SelectItem>
                      <SelectItem value="referral">Personal Referral</SelectItem>
                      <SelectItem value="directory">Directory/Listing</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="section-divider" />

            {/* Arrangement Details */}
            <div>
              <T k="inquiry.arrangement-details" as="h3" className="font-heading text-2xl text-charcoal mb-6">Arrangement Details</T>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="preferred_city" className="font-body text-sm text-stone-grey">
                    Preferred City *
                  </Label>
                  <Input
                    id="preferred_city"
                    name="preferred_city"
                    value={formData.preferred_city}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Los Angeles, New York City, Miami"
                    className="form-input"
                    data-testid="inquiry-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="desired_duration" className="font-body text-sm text-stone-grey">
                    Desired Duration *
                  </Label>
                  <Input
                    id="desired_duration"
                    name="desired_duration"
                    value={formData.desired_duration}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 3 hours, overnight, weekend"
                    className="form-input"
                    data-testid="inquiry-duration"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="screening_method" className="font-body text-sm text-stone-grey">
                    Preferred Screening Method *
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange("screening_method", value)} required>
                    <SelectTrigger className="form-input" data-testid="inquiry-screening">
                      <SelectValue placeholder="Select method..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="provider_references">Provider References</SelectItem>
                      <SelectItem value="professional_verification">Professional Verification (LinkedIn)</SelectItem>
                      <SelectItem value="review_sites">Review Sites (PD, TNA, P411)</SelectItem>
                      <SelectItem value="alternative">Alternative Screening</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_date" className="font-body text-sm text-stone-grey">
                    Preferred Date/Timeframe
                  </Label>
                  <Input
                    id="preferred_date"
                    name="preferred_date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    placeholder="e.g., March 15th evening, or flexible within next 2 weeks"
                    className="form-input"
                    data-testid="inquiry-date"
                  />
                </div>
              </div>
            </div>

            <div className="section-divider" />

            {/* Message */}
            <div>
              <T k="inquiry.tell-me-about" as="h3" className="font-heading text-2xl text-charcoal mb-6">Tell Me About Yourself</T>
              <div className="space-y-2">
                <Label htmlFor="message" className="font-body text-sm text-stone-grey">
                  Your Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Share a bit about yourself, what you're looking for in our time together, and any relevant details about verification/references..."
                  className="form-input min-h-[150px] resize-none"
                  data-testid="inquiry-message"
                />
                <T k="inquiry.the-more-thoughtful" as="p" className="font-body text-xs text-taupe">The more thoughtful detail you provide, the better I can assess our compatibility.</T>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="glass rounded-xl p-6">
              <p className="font-body text-xs text-stone-grey leading-relaxed">
                <T k="inquiry.disclaimer" className="font-medium">Disclaimer:</T> By submitting this inquiry, you confirm that you are 18 years or older 
                and understand that any donation exchanged is solely for time and companionship. You agree to be respectful 
                and conduct yourself appropriately throughout our communication.
              </p>
            </div>

            {/* Submit */}
            <div className="text-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary min-w-[200px]"
                data-testid="submit-inquiry-btn"
              >
                {isSubmitting ? "Submitting..." : "Submit Inquiry"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BookingInquiry;
